import { useRef } from "react";
import { TextField, Box, Typography } from "@mui/material";
import Icon from "../Icon";
import { useDateRangePicker } from "./dateRangePickerData";
import "./index.scss";

const DateRangePicker = ({
  fromDate = null,
  toDate = null,
  onChange,
  className = "",
  inputClassName = "",
  containerClassName = "",
  disabled = false,
  minDate = null,
  maxDate = null,
  allowFutureDates = false, // New prop: by default future dates are disabled
  dateFormat = "dd/MM/yyyy",
  label = "",
  required = false,
  showClearButton = true,
  fromLabel = "From Date",
  toLabel = "To Date",
  fromPlaceholder = "Select start date",
  toPlaceholder = "Select end date",
  ...otherProps
}) => {
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  // Use the custom hook for all logic
  const {
    errors,
    errorMessage,
    formattedFromDate,
    formattedToDate,
    formattedMinDate,
    formattedMaxDate,
    handleFromDateChange,
    handleToDateChange,
  } = useDateRangePicker({
    fromDate,
    toDate,
    onChange,
    minDate,
    maxDate,
    dateFormat,
    allowFutureDates,
  });

  return (
    <Box
      className={`date-range-picker ${containerClassName} ${className} ${
        errorMessage ? "date-range-picker_error_box" : ""
      }`}
      role="group"
      aria-labelledby={label ? "date-range-label" : undefined}
      aria-describedby={errorMessage ? "date-range-error" : undefined}
    >
      {label && (
        <Typography
          id="date-range-label"
          variant="subtitle2"
          className="date-range-picker__label"
          component="label"
        >
          {label}
          {required && <span className="date-range-picker__required">*</span>}
        </Typography>
      )}

      <Box className="date-range-picker__inputs">
        <TextField
          ref={fromDateRef}
          type="date"
          label={fromLabel}
          value={formattedFromDate}
          onChange={handleFromDateChange}
          placeholder={fromPlaceholder}
          disabled={disabled}
          size="small"
          variant="outlined"
          className={`date-range-picker__input ${inputClassName}`}
          error={!!(errors.fromDate || errors.range)}
          inputProps={{
            min: formattedMinDate,
            max: allowFutureDates ? null : formattedMaxDate,
            "aria-label": `${fromLabel}${required ? " (required)" : ""}`,
            "aria-describedby": errors.fromDate ? "from-date-error" : undefined,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          {...otherProps}
        />

        <Box className="date-range-picker__separator" aria-hidden="true">
          <Icon iconName="arrowRight" width={16} height={16} />
        </Box>

        <TextField
          ref={toDateRef}
          type="date"
          label={toLabel}
          value={formattedToDate}
          onChange={handleToDateChange}
          placeholder={toPlaceholder}
          disabled={disabled}
          size="small"
          variant="outlined"
          className={`date-range-picker__input ${inputClassName}`}
          error={!!(errors.toDate || errors.range)}
          inputProps={{
            min: formattedMinDate,
            max: allowFutureDates ? null : formattedMaxDate,
            "aria-label": `${toLabel}${required ? " (required)" : ""}`,
            "aria-describedby": errors.toDate ? "to-date-error" : undefined,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          {...otherProps}
        />
      </Box>

      {errorMessage && (
        <Typography
          id="date-range-error"
          variant="caption"
          className="date-range-picker__error"
          role="alert"
          aria-live="polite"
        >
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default DateRangePicker;
